<script setup lang="ts">
import type { Sales, SalesAttributes, SalesData } from "~/layers/media/types/Sales";
import type { SalesPage, SalesPageBlockSettings } from "~/layers/media/types/SalesPage";
import type { ProjectPageData } from "~/layers/projects/types/project-page";
import { SIMILAR_SALES_LIMIT, useSimilarSales } from "~/layers/media/composables/useSimilarSales";
import SubscribeForm from "../../../fos/components/subscribe/SubscribeForm.vue";

const { section } = useThemeColor("dark");

const { find, findOne } = useStrapi();
const route = useRoute();

const { data: sales } = await useAsyncData(`sales-${route.params.slug}`, () =>
  find<Sales>("sales", {
    filters: {
      slug: {
        $eq: route.params.slug,
      },
    },
    populate: {
      // slug нужен блоку «Доступно в проектах»: по нему карточка проекта находится в каталоге
      projects: {
        fields: ["title", "slug"],
      },
      other_sales: {
        populate: {
          cover: "*",
          projects: {
            fields: ["title"],
          },
        },
      },
      cover: "*",
      seo: {
        populate: "*",
      },
      blocks: {
        on: {
          "content.rich-content": {
            populate: "*",
          },
          "content.picture": {
            populate: "*",
          },
          "content.gallery": {
            populate: "*",
          },
          "content.video": {
            populate: "*",
          },
          "content.quoted": {
            populate: "*",
          },
          "content.button": {
            populate: "*",
          },
          "content.table": {
            populate: "*",
          },
          "content.slider": {
            populate: "*",
          },
        },
      },
    },
  }),
);

// Записи с таким слагом нет — отдаём оформленную 404, а не 500 на первом же
// обращении к пустым данным (IT-797, п. 4).
if (!sales.value?.data?.[0]) {
  throw createError({ statusCode: 404, statusMessage: "Sale not found", fatal: true });
}

// Блок обратной связи одинаковый для всех акций, поэтому лежит не в самой акции,
// а в single type «Страница акций»: заголовок, подзаголовок и фото меняются в
// админке без правки кода (IT-1374). Запись может быть ещё не создана — тогда
// findOne отдаёт 404, и блок собирается из значений ниже.
const { data: salesPage } = await useAsyncData("sales-page", () =>
  findOne<SalesPage>("sales-page", {
    populate: {
      fos_block: {
        populate: "*",
      },
      projects_block: "*",
      other_sales_block: "*",
    },
  }).catch(() => null),
);

const FOS_FALLBACK = {
  title: "получить консультацию",
  subtitle: "Оставьте контакты и получите выгодные условия покупки!",
  imageSrc: "https://s3.timeweb.cloud/37651e87-s3-atlas/139_resized_81f9a70f6a_edited_0eb2da71cd.webp",
};

// Блоки «Доступно в проектах» и «Еще акции» включаются и переименовываются в админке
// («Страница акций»). Настройки нет — блок показывается с заголовком по умолчанию.
const blockSettings = (settings: SalesPageBlockSettings | null | undefined, defaultTitle: string) => ({
  enabled: settings?.enabled ?? true,
  title: settings?.title || defaultTitle,
});

const getFosContent = computed(() => salesPage.value?.data?.attributes?.fos_block ?? FOS_FALLBACK);
const getProjectsBlock = computed(() =>
  blockSettings(salesPage.value?.data?.attributes?.projects_block, "Доступно в проектах"),
);
const getOtherSalesBlock = computed(() =>
  blockSettings(salesPage.value?.data?.attributes?.other_sales_block, "Еще акции"),
);

const getSales = computed<SalesAttributes>(() => sales.value?.data?.[0]?.attributes);
const getTags = computed<ProjectPageData[]>(() => getSales.value?.projects.data);
const getProjectSlugs = computed<string[]>(() =>
  (getSales.value?.projects?.data ?? [])
    .map((project: ProjectPageData) => project.attributes?.slug)
    .filter((slug): slug is string => !!slug),
);

// В админке акции можно выбрать другие акции руками; недостающие до трёх добираем сами —
// сначала похожие, у которых совпадает больше проектов (IT-1377).
const manualOtherSales = computed<SalesData[]>(() => getSales.value?.other_sales?.data ?? []);
const similarSales =
  getOtherSalesBlock.value.enabled && manualOtherSales.value.length < SIMILAR_SALES_LIMIT
    ? await useSimilarSales({
        slug: String(route.params.slug),
        projectIds: getSales.value?.projects?.data?.map((project: ProjectPageData) => project.id) ?? [],
        limit: SIMILAR_SALES_LIMIT,
      })
    : ref<SalesData[]>([]);

const getOtherSales = computed<SalesData[]>(() => {
  const chosen = [...manualOtherSales.value];
  for (const sale of similarSales.value) {
    if (chosen.length >= SIMILAR_SALES_LIMIT) break;
    if (!chosen.some((item) => item.id === sale.id)) chosen.push(sale);
  }

  // type говорит карточке, что это акция: от него зависят бейдж и ссылка (/sales/<слаг>)
  return chosen.slice(0, SIMILAR_SALES_LIMIT).map((item: SalesData) => ({
    ...item,
    attributes: {
      ...item.attributes,
      type: "Акции",
    },
  }));
});

const seoData = computed(() => getSales.value?.seo);
const seoTitle = computed(() => seoData.value?.metaTitle?.trim() || getSales.value?.title);
const seoDescription = computed(() => seoData.value?.metaDescription?.trim() || getSales.value?.description);
const seoImage = computed(
  () => seoData.value?.image?.data?.attributes?.url || getSales.value?.cover?.data?.attributes?.url || "",
);

const META_DATA = computed(() => {
  return {
    og: {
      component: "Frame",
      title: `Акция | «Атлас Девелопмент»`,
      description: "Амбициозная команда профессионалов, успешно работающая на рынке девелопмента с 2006 года",
      logo: "https://atlasgroup.su/uploads/main/009/bf4c6ab88c322a476fed0d22e3ef5bf9f5782259ca962d56edea638edc678ece/logo.svg",
      url: "www.atlasgroup.su",
      bg: "linear-gradient(to bottom right, #091536, black)",
    },
    meta: {
      title: seoTitle.value,
      "theme-color": "#ffffff",
      description: seoDescription.value,
      author: "Атлас Девелопмент",
      robots: "index, follow",
      og_title: seoTitle.value,
      og_description: seoDescription.value,
      og_image: seoImage.value,
      og_url: "https://atlasdevelopment.su/",
      og_type: "article",
      article_published_time: getSales.value?.publication_date || getSales.value?.createdAt,
      article_modified_time: getSales.value?.updatedAt,
      tw_title: seoTitle.value,
      tw_description: seoDescription.value,
      tw_url: "https://atlasdevelopment.su/",
    },
  };
});
useMetaData(META_DATA);

useHead({
  script: [
    {
      type: "application/ld+json",
      children: computed(() =>
        JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: getSales.value?.title,
            description: getSales.value?.description,
            url: `https://atlasdevelopment.su/sales/${route.params.slug}`,
            datePublished: getSales.value?.publication_date,
            dateModified: getSales.value?.updatedAt,
            inLanguage: "ru-RU",
            image: getSales.value?.cover?.data?.attributes?.url,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", ".article-header", ".answer-first p"],
            },
            author: {
              "@type": "Organization",
              "@id": "https://atlasdevelopment.su/#organization",
              name: "Атлас Девелопмент",
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://atlasdevelopment.su/#organization",
              name: "Атлас Девелопмент",
              logo: {
                "@type": "ImageObject",
                url: "https://atlasdevelopment.su/Logo_white.svg",
              },
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Главная",
                item: "https://atlasdevelopment.su/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Акции",
                item: "https://atlasdevelopment.su/sales",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: getSales.value?.title,
              },
            ],
          },
        ]),
      ),
    },
  ],
});
</script>

<template>
  <section ref="section" class="mt-20 2xl:mt-32 mb-2">
    <Container>
      <div class="flex flex-col md:grid md:grid-cols-4 gap-5 w-full">
        <!-- <div>
          <ButtonBack fallback-url="/news"> Назад </ButtonBack>
        </div> -->
        <div class="md:col-span-3 md:pb-10">
          <Heading as="h1" variant="5" text-transform="uppercase">
            {{ getSales?.title }}
          </Heading>
        </div>
        <div class="col-span-full relative">
          <Line />
        </div>
        <div class="md:col-span-1 md:pr-20 pt-4">
          <Text variant="14" text-transform="uppercase" class="opacity-85 mb-4"> Акция </Text>
          <time
            v-if="getSales?.publication_date"
            :datetime="getSales.publication_date"
            class="block text-14 opacity-60 mb-4"
          >
            {{ getSales.publication_date?.split("-").reverse().join(".") }}
          </time>
          <div class="flex flex-wrap gap-2">
            <Badge v-for="({ attributes }, index) in getTags" :key="index" variant="third" class="h-min">
              {{ attributes?.title }}
            </Badge>
          </div>
        </div>
        <div class="md:col-span-3 media-content w-full">
          <ContentBlocksRenderer :blocks="getSales.blocks" />
        </div>
      </div>
    </Container>
    <AvailableProjects v-if="getProjectsBlock.enabled" :title="getProjectsBlock.title" :slugs="getProjectSlugs" />
    <FosSection :content="getFosContent" :form-component="SubscribeForm" />
    <div v-if="getOtherSalesBlock.enabled && getOtherSales.length" class="pt-20 pb-[100px] md:py-[120px]">
      <Container>
        <Heading as="h2" variant="3" class="leading-[100%] mb-10">{{ getOtherSalesBlock.title }}</Heading>
        <ul class="max-lg:hidden grid lg:grid-cols-3 gap-5 list-none p-0 m-0">
          <li v-for="sale in getOtherSales" :key="sale.id" class="h-full">
            <MediaItem :media="sale.attributes" title-as="div" />
          </li>
        </ul>
        <Carousel class="lg:hidden" :opts="{ align: 'start' }">
          <CarouselContent viewport-class="-mx-4 px-4 sm:-mx-8 sm:px-8" class="-ml-2.5">
            <CarouselItem v-for="sale in getOtherSales" :key="sale.id" class="pl-2.5 basis-[90%] sm:basis-1/2">
              <MediaItem :media="sale.attributes" title-as="div" />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </Container>
    </div>
  </section>
</template>

<style>
table,
tbody,
colgroup {
  @apply rounded-[8px];
}
table,
tbody,
tr {
  @apply w-full border divide-x;
}

td {
  @apply p-2 min-w-32;
}
</style>
